import { useState } from "react"
import classNames from "classnames"
import { ButtonPrimary, ButtonSecondary, IconButton } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { Dropdown } from "@nova/component-dropdown"
import { createQuery, QueryableResults } from "@nova/queryable"
import { usePilotTrackingQuery } from "features/pilot-tracking/PilotTrackingQueries"
import { useDeleteCurrencyPolicy } from "features/pilot-tracking/currency/CurrencyPolicyQueries"
import {
    summarizeCurrencyStatuses,
    usePilotCurrencyStatuses,
} from "features/pilot-tracking/currency/usePilotCurrencyStatuses"
import {
    getGenericConfirmationModalComponent,
    useConfirmationModalContext,
} from "features/modals/WorkspaceConfirmationModalV2"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { useAppDispatch } from "integrations/redux/hooks"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { useRouter } from "pages"
import { pluralize } from "@nova/util-strings"
import { getGenericWorkspacePage } from "../WorkspaceRoutes"
import { AlertContainer } from "@nova/component-alert-container"

const currencyPoliciesQuery = createQuery({
    resource: "currency_policies",
    select: ["id", "name", "metric", "threshold", "window_days"],
    aggregates: [{ field: "user_links.user_id", func: "COUNT", alias: "user_count" }],
})

type CurrencyPolicies = QueryableResults<typeof currencyPoliciesQuery>

export const CurrencyPage = getGenericWorkspacePage({
    Header: CurrencyPageHeader,
    Content: CurrencyPageContent,
    maxWidth: 1200,
})

function CurrencyPageHeader() {
    const router = useRouter("/workspace/:workspaceId")
    const { isEditor } = useWorkspacePermissions()

    return (
        <div className="flex justify-between items-center h-full">
            <div className="text-[1.375rem] telegraf flex items-center">Currency</div>
            {isEditor && (
                <router.Link to={{ path: "/fleet/pilots/currency/create" }}>
                    <ButtonPrimary title="Policy" LeftIcon={Icons.Plus} />
                </router.Link>
            )}
        </div>
    )
}

function CurrencyPageContent() {
    const router = useRouter("/workspace/:workspaceId")
    const { data, isLoading } = usePilotTrackingQuery(currencyPoliciesQuery)
    const { isEditor } = useWorkspacePermissions()

    if (isLoading) {
        return (
            <div className="w-full h-full flex justify-center items-center">
                <div className="loader" />
            </div>
        )
    }

    if (!data?.length) {
        return (
            <div className="flex flex-col items-center gap-5 pt-[60px] text-center">
                <div className="flex flex-col gap-2 items-center">
                    <div className="text-[25px] text-white">Keep Pilots Current</div>
                    <div className="text-sm text-gray-300">
                        Set flight requirements to pilots to keep them current and deployable.
                    </div>
                </div>
                {isEditor && (
                    <router.Link to={{ path: "/fleet/pilots/currency/create" }}>
                        <ButtonPrimary title="Policy" LeftIcon={Icons.Plus} />
                    </router.Link>
                )}
            </div>
        )
    }

    return (
        <div className="w-full text-white flex flex-col gap-4">
            <OverduePilotsBanner />
            <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between pl-6 pr-5 pb-1 font-mono text-[12px] text-gray-400">
                    <div>Name</div>
                    <div className="flex items-center justify-end">
                        <div className="w-[106px]">Requirement</div>
                        <div className="w-[75px]">Period</div>
                    </div>
                </div>
                {data.map((p) => (
                    <CurrencyPolicyRow key={p.id} policy={p} />
                ))}
            </div>
        </div>
    )
}

const OverduePilotsBanner = () => {
    const router = useRouter("/workspace/:workspaceId")
    const { statusesByUser } = usePilotCurrencyStatuses()
    const [dismissed, setDismissed] = useState(false)

    const overdueCount = [...statusesByUser.values()].filter(
        (statuses) => summarizeCurrencyStatuses(statuses).state === "overdue"
    ).length

    if (dismissed || overdueCount === 0) return null

    return (
        <AlertContainer
            type="warning"
            variant="banner"
            title={`${pluralize({ count: overdueCount, str: "Pilot" })} ${overdueCount === 1 ? "is" : "are"} overdue`}
            action={
                <>
                    <span className="text-gray-400">•</span>
                    <button
                        type="button"
                        className="flex items-center gap-1.5 text-sm text-white hover:underline"
                        onClick={() => router.navigate({ path: "/fleet/pilots" })}
                    >
                        Review
                        <Icons.SmallIcon Icon={Icons.ArrowRight} />
                    </button>
                </>
            }
            onClose={() => setDismissed(true)}
        />
    )
}

const CurrencyPolicyRow = ({ policy }: { policy: CurrencyPolicies[number] }) => {
    const router = useRouter("/workspace/:workspaceId")
    const { isEditor } = useWorkspacePermissions()
    const { deleteCurrencyPolicy } = useDeleteCurrencyPolicy()
    const confirmationModal = useConfirmationModalContext()
    const dispatch = useAppDispatch()
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)

    const handleDelete = () => {
        confirmationModal.confirm({
            RenderComponent: getGenericConfirmationModalComponent({
                title: "Delete Policy?",
                message: `Delete ${policy.name}?`,
                confirmText: "Delete Policy",
            }),
            asyncConfirmCallback: async () => {
                try {
                    await deleteCurrencyPolicy(policy.id)
                    dispatch(
                        setGlobalNotificationContext({
                            message: `${policy.name} has been deleted.`,
                            status: GlobalNotificationStatus.success,
                            timeoutInMs: 5000,
                        })
                    )
                } catch {
                    dispatch(
                        setGlobalNotificationContext({
                            message: `Failed to delete ${policy.name}. Please try again.`,
                            status: GlobalNotificationStatus.error,
                            timeoutInMs: 5000,
                        })
                    )
                }
            },
        })
    }

    const userCount = Number(policy.user_count)

    return (
        <div
            className={`group flex items-center gap-10 h-[78px] px-6 py-4 border border-white-100 rounded-xl transition-colors ${isDropdownOpen ? "bg-white-100" : "hover:bg-white-100"}`}
        >
            <div className="grow flex flex-col gap-1 min-w-0">
                <div className="text-[18px] text-gray-0">{policy.name}</div>
                <small className="text-gray-100">
                    Applies to: {pluralize({ count: userCount, str: "pilot" })}
                </small>
            </div>
            <div className="flex items-center overflow-hidden">
                <div className="w-[106px] text-[18px] text-gray-100 whitespace-nowrap">
                    {policy.threshold} {policy.metric}
                </div>
                <div className="w-[75px] text-[18px] text-gray-100 whitespace-nowrap">
                    {policy.window_days} days
                </div>
                {isEditor && (
                    <div
                        className={classNames(
                            "flex items-center gap-2 transition-[width,opacity]",
                            {
                                "w-[100px]": isDropdownOpen,
                                "opacity-0 group-hover:opacity-100 w-0 group-hover:w-[100px]":
                                    !isDropdownOpen,
                            }
                        )}
                    >
                        <router.Link
                            to={{
                                path: "/fleet/pilots/currency/:policyId",
                                params: { policyId: policy.id },
                            }}
                        >
                            <ButtonSecondary title="Edit" LeftIcon={Icons.Edit} />
                        </router.Link>
                        <Dropdown onOpenChange={setIsDropdownOpen}>
                            <Dropdown.Trigger>
                                <IconButton className="text-gray-100">
                                    <Icons.SmallIcon Icon={Icons.MoreVert} />
                                </IconButton>
                            </Dropdown.Trigger>
                            <Dropdown.Menu position="bottom-right">
                                <Dropdown.ButtonItem
                                    className="flex items-center gap-3 !text-red-500"
                                    onClick={handleDelete}
                                >
                                    <Icons.SmallIcon Icon={Icons.Trash} />
                                    Delete
                                </Dropdown.ButtonItem>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                )}
            </div>
        </div>
    )
}
